import {
  Box,
  Button,
  Collapse,
  Drawer,
  Group,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FilterOption } from '@/components/common';
import {
  DateRangeField,
  FilterIconButton,
  FilterOptionButton,
  FilterSection,
  filterSheetStyles
} from '@/components/common';

interface PmMobileFiltersProps {
  periodOptions: FilterOption[];
  period: string | null;
  onPeriodChange: (value: string) => void;
  dateRange: [string | null, string | null];
  onDateRangeChange: (value: [string | null, string | null]) => void;
  statusOptions: FilterOption[];
  statusFilter: string | null;
  onStatusChange: (value: string) => void;
  isActive: boolean;
  onReset: () => void;
  resultCount: number;
}

export const PmMobileFilters = ({
  periodOptions,
  period,
  onPeriodChange,
  dateRange,
  onDateRangeChange,
  statusOptions,
  statusFilter,
  onStatusChange,
  isActive,
  onReset,
  resultCount
}: PmMobileFiltersProps) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  return (
    <>
      <FilterIconButton
        isActive={isActive}
        onClick={() => setOpened(true)}
        label={t('pmDashboard.filters')}
      />

      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="bottom"
        title={
          <Group justify="space-between" wrap="nowrap" w="100%">
            <Text fz={15} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {t('pmDashboard.filters')}
            </Text>
            {isActive && (
              <UnstyledButton
                onClick={onReset}
                fz={14}
                fw={600}
                style={{ color: 'var(--color-text-link)' }}
              >
                {t('pmDashboard.filtersReset')}
              </UnstyledButton>
            )}
          </Group>
        }
        styles={filterSheetStyles}
      >
        <Stack gap={20}>
          <FilterSection title={t('pmDashboard.filterPeriod')}>
            <div>
              <SimpleGrid cols={2} spacing={8}>
                {periodOptions.map((option) => (
                  <FilterOptionButton
                    key={option.value}
                    label={option.label}
                    selected={option.value === period}
                    onClick={() => onPeriodChange(option.value)}
                  />
                ))}
              </SimpleGrid>
              <Collapse in={period === 'CUSTOM'} transitionDuration={200}>
                <Box pt={18}>
                  <DateRangeField
                    label={t('pmDashboard.filterDateRange')}
                    valueFormat="DD.MM.YYYY."
                    value={dateRange}
                    onChange={onDateRangeChange}
                    clearable
                  />
                </Box>
              </Collapse>
            </div>
          </FilterSection>

          <FilterSection title={t('pmDashboard.filterStatus')}>
            <SimpleGrid cols={2} spacing={8}>
              {statusOptions.map((option) => (
                <FilterOptionButton
                  key={option.value}
                  label={option.label}
                  selected={option.value === statusFilter}
                  onClick={() => onStatusChange(option.value)}
                />
              ))}
            </SimpleGrid>
          </FilterSection>

          <Button fullWidth onClick={() => setOpened(false)}>
            {t('pmDashboard.filtersShow', { count: resultCount })}
          </Button>
        </Stack>
      </Drawer>
    </>
  );
};
